import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LassoSimpleButtonFull, LassoToggle } from "../../components/ui/utils/LassoButtons";
import { Roles } from "../../shared/common/SharedUtils";
import { logDebug, logError } from "../../shared/common/logger";
import { getAppStore, getUserStore } from "../../data/DataStores";
import LoadingAnimation from "../../components/ui/utils/LoadingAnimation";
import "./LasssoMVPOrganizationSettingsPage.scss";
import { observer } from "mobx-react";
import { IOrganizationCreateObject, IOrganizationCreateUser } from "../../shared/common/interfaces";
import TopMostPortal from "../../components/ui/utils/Portals";
import LassoMVPUserSettingsModal from "../components/LassoMVPUserSettingsModal";
import DeleteUserModal from "../components/DeleteUserModal";
import RestAPI from "../../utils/restAPI";
import { IOrganizationUser } from "../../shared/common/interfaces";
import SecureImage from "../../components/ui/utils/SecureImage";
import { SelectOption } from "../components/EstimateEngine/ErrorResolutionPanel";
import { localTPAStore, NewRuleScreen } from "./LassoMPVOrganizationTPARules";
import { toastNotify } from "../../shared/client/FrontendUtils";
import { gTPARuleTypes } from "../../shared/common/EstimateEngine/EstimateEngineTypes";
import { TicType1CatToCellMap } from "../components/EstimateEngine/EstimateTable/LineEditingDialog";
import FeatureFlagsModal, { FEATURE_FLAGS } from "../components/FeatureFlagsModal";
import PDFExportConfigModal from "../components/PDFExportConfigModal";
import CRMIntegrationsModal from "../components/CRMIntegrationsModal";
import {
	buildOrganizationCustomSettings,
	DEFAULT_ENABLED_NOTE_FLOW_TYPES,
	EnabledNoteFlowTypes,
	estimate_types,
	GeneralRoomOrder,
	getProfitOverheadMap,
	getGeneralRoomOrderFromSettings,
	getEnabledNoteFlowTypesFromSettings,
	parseOrganizationCustomSettings,
	ProfitOverheadFormValues,
} from "./organizationCustomSettings";

const LassoMVPUserTable: React.FC = observer(() => {
	const appStore = getAppStore();
	const [editingUser, setEditingUser] = useState<IOrganizationCreateUser | null>(null);
	const [userToDelete, setUserToDelete] = useState<IOrganizationUser | null>(null);
	return (
		<div className="w-[880px] max-h-[540px] overflow-y-auto pt-6 bg-white rounded-2xl shadow border border-gray-200 flex-col justify-start items-start gap-6 inline-flex">
			<div className="self-stretch px-6 justify-between items-center inline-flex">
				<div className="text-gray-900 text-xl font-semibold font-['Hanken Grotesk'] leading-7">
					Organization users ({appStore.selectedOrganization!.users?.length || 0})
				</div>
				<div className="w-[231px] justify-end items-center gap-6 flex">
					<div className="justify-end items-center flex">
						<div className="w-11 h-11 bg-white rounded-lg border border-gray-200 justify-center items-center gap-2 flex">
							<img className="w-5 h-5 relative" src="/assets/ui/search-lg.svg" />
						</div>
					</div>
					<div className="h-11 self-stretch origin-top-left border border-gray-200"></div>
					<LassoSimpleButtonFull
						caption="Add New User"
						onClick={() => {
							setEditingUser({
								role: (appStore.selectedOrganization!.users ?? []).length === 0 ? Roles.ADMIN_ROLE : Roles.USER_ROLE,
								email: "",
								firstName: "",
								lastName: "",
								photoURL: "",
							});
						}}
					/>
				</div>
			</div>
			<div className="w-full ">
				<div className="flex bg-gray-50 border-t border-b border-gray-200 px-6 py-4">
					<div className="table-head w-[35%] ">Name</div>
					<div className="table-head w-[15%]">Role</div>
					<div className="table-head w-[30%] ">Email</div>
				</div>
				<div className="flex  flex-col">
					{appStore.selectedOrganization?.users?.map((user, index) => {
						return (
							<div key={index} className="w-full flex table-row bg-gray-50 border-t border-b border-gray-200 px-6 py-4">
								<div className="table-text w-[35%]">{`${user.firstName || ""} ${user.lastName || ""}`}</div>
								<div className="table-text w-[15%]">{user.role}</div>
								<div className="table-text w-[30%]">{user.email}</div>
								<button
									className="only-hover-edit"
									onClick={() => {
										setEditingUser(user as IOrganizationCreateUser);
									}}
								>
									<img src="/assets/ui/edit-03.svg"></img>
								</button>
								<button
									className="only-hover-trash"
									onClick={() => {
										setUserToDelete(user as IOrganizationUser);
									}}
								>
									<img src="/assets/ui/trash-01.svg"></img>
								</button>
							</div>
						);
					})}
				</div>
			</div>
			{editingUser && (
				<TopMostPortal>
					<LassoMVPUserSettingsModal
						usersForValidation={appStore.selectedOrganization!.users}
						editingUser={editingUser}
						isNewUser={!editingUser.email}
						cancel={() => setEditingUser(null)}
						update={async (editedUser) => {
							if (appStore.selectedOrganization?.users && appStore.selectedOrganization?.organizationId) {
								// Check if this is an existing user (has lassoUserId) or new user
								const existingUser = appStore.selectedOrganization.users.find(
									(u) =>
										(u as IOrganizationUser).lassoUserId &&
										(u as IOrganizationUser).lassoUserId === (editingUser as IOrganizationUser).lassoUserId,
								) as IOrganizationUser | undefined;

								if (existingUser && existingUser.lassoUserId) {
									try {
										await RestAPI.fetchAPI("/rest/access/editUser", "POST", {
											organizationId: appStore.selectedOrganization.organizationId,
											lassoUserId: existingUser.lassoUserId,
											email: editedUser.email,
											role: editedUser.role,
											firstName: editedUser.firstName || "",
											lastName: editedUser.lastName || "",
											photoURL: editedUser.photoURL,
										});

										// Update user in the local state
										const existingUserIndex = appStore.selectedOrganization.users.findIndex(
											(u) => (u as IOrganizationUser).lassoUserId === existingUser.lassoUserId,
										);

										if (existingUserIndex !== -1) {
											appStore.selectedOrganization.users[existingUserIndex] = {
												...existingUser,
												...editedUser,
											} as IOrganizationUser;
										}

										toastNotify("User successfully updated", false);
									} catch (err) {
										logError("Error updating user", err);
										toastNotify("Failed to update user. Please try again.", true);
										return; // Don't close modal on error
									}
								} else {
									// Add new user
									//@ts-ignore TODO :: fix this
									appStore.selectedOrganization.users.push(editedUser);
								}
							} else {
								if (appStore.selectedOrganization && appStore.selectedOrganization.users) {
									appStore.selectedOrganization.users.push(editedUser as IOrganizationUser);
								}
							}
							setEditingUser(null);
						}}
					/>
				</TopMostPortal>
			)}
			{userToDelete && appStore.selectedOrganization?.organizationId && (
				<DeleteUserModal
					user={userToDelete}
					organizationId={appStore.selectedOrganization.organizationId}
					onCancel={() => {
						setUserToDelete(null);
					}}
					onSuccess={() => {
						// Remove user from the organization's users array
						if (appStore.selectedOrganization?.users) {
							appStore.selectedOrganization.users = appStore.selectedOrganization.users.filter(
								(u) => u.lassoUserId !== userToDelete.lassoUserId,
							);
						}
						setUserToDelete(null);
					}}
				/>
			)}
		</div>
	);
});

const LassoOrganizationSettings: React.FC<{ startInEditMode: boolean }> = observer(({ startInEditMode }) => {
	const userStore = useRef(getUserStore());
	const appStore = useRef(getAppStore());
	const [isEditing, setIsEditing] = useState(startInEditMode);
	const [allowDomain, setAllowDomain] = useState(!!appStore.current.selectedOrganization!.allowedDomain);
	const [showFeatureFlagsModal, setShowFeatureFlagsModal] = useState(false);
	const [showCRMIntegrationsModal, setShowCRMIntegrationsModal] = useState(false);

	const handleSaveFeatureFlags = async (flags: { [key: string]: boolean }) => {
		try {
			// Ensure all feature flags are included with true/false values
			const allFlags: { [key: string]: boolean } = {};
			FEATURE_FLAGS.forEach((flag) => {
				allFlags[flag.key] = flags[flag.key] || false;
			});

			const organizationId = appStore.current.selectedOrganization!.organizationId;
			await RestAPI.fetchAPI(`/rest/access/organization/${organizationId}/features`, "PUT", {
				featureFlags: allFlags,
			});

			// Reload organizations to get updated data
			await appStore.current.loadOrganizations(true);

			toastNotify("Feature flags saved successfully", false);
			setShowFeatureFlagsModal(false);
		} catch (err) {
			logError("Error saving feature flags", err);
			toastNotify("Error saving feature flags", true);
			throw err; // Re-throw so modal can handle loading state
		}
	};

	// useEffect(() => {
	// 	let valid = true;
	// 	try {
	// 		JSON.parse(aiModels);
	// 	} catch (e) {
	// 		valid = false;
	// 	}
	// 	if (allowDomain) {
	// 		valid = valid && SharedUtils.isValidDomain(domain);
	// 	}
	// 	valid = valid && appStore.selectedOrganization!.organizationName.length > 0 && organizationDescription.length > 0;
	// 	// valid = valid && users.filter(u => u.role === Roles.ADMIN_ROLE).length > 0;
	// 	setDataValid(valid);
	// }, [allowDomain, domain, aiModels, organizationDescription, appStore.selectedOrganization]);

	return (
		appStore.current.selectedOrganization && (
			<>
				<div className="max-w-[448px] overflow-y-auto h-[571px] flex-col justify-start items-start gap-5 inline-flex">
					<div className="self-stretch pb-4 border-b border-gray-200 flex-col justify-start items-stretch gap-3 inline-flex">
						<div className="self-stretch justify-between items-center inline-flex">
						{/* <input key={forceRender} type="file" id="files" accept="image/*" ref={uploadRef} style={{ display: 'none' }}
				onChange={async (e) => {
					if (uploadRef.current!.files && uploadRef.current!.files.length > 0) {
						// TODO :: loading animation
						const formData = new FormData();
						formData.append('destination', `organizations/${userStore.current.user?.organizationId}/users/${userStore.current.user?.lassoUserId}/private`);
						for (let i = 0; i < uploadRef.current!.files.length; i++) {
							formData.append('files', uploadRef.current!.files[i]);
						}
						try {
							const data = await RestAPI.fetchAPI('/rest/files/upload', 'POST', formData);
							appStore.current.selectedOrganization!.organizationLogo = data[0].storagePath;
							logDebug('Upload dest:', data[0].storagePath);
						} catch (error) {
							logError('Error uploading files', error);
						}
						setForceRender(forceRender + 1);
					}
				}}
			/> */}
						{/* TODO :: modify class so there will be no straching of the image */}
						<div className={`relative w-[183px] h-[60px] justify-start items-center flex `}>
							<SecureImage
								onUrlChange={(url) => (appStore.current.selectedOrganization!.organizationLogo = url)}
								key={isEditing.toString()}
								destination={
									isEditing
										? `organizations/${userStore.current.user?.organizationId}/users/${userStore.current.user?.lassoUserId}/private`
										: undefined
								}
								w={183}
								h={60}
								defaultImage={
									appStore.current.selectedOrganization!.organizationLogo
										? `${RestAPI.getStorageFileUrl(appStore.current.selectedOrganization!.organizationLogo)}`
										: "/assets/ui/org-placeholder.png"
								}
							/>
						</div>
						<div
							onClick={() => {
								setIsEditing(true);
							}}
							className="cursor-pointer w-10 p-2.5 bg-white rounded-lg shadow border border-gray-300 justify-center items-center gap-2 flex"
						>
							<img src="/assets/ui/edit-03.svg"></img>
						</div>
						</div>
						<div className="self-stretch justify-start items-center gap-2 flex-wrap inline-flex">
							<div className="btn">
								<LassoSimpleButtonFull
									caption="TPA Rules"
									onClick={() => {
										localTPAStore.showTPA = true;
									}}
								/>
							</div>
							<div className="btn">
								<LassoSimpleButtonFull
									caption="Flags"
									onClick={() => {
										setShowFeatureFlagsModal(true);
									}}
								/>
							</div>
							{appStore.current.selectedOrganization?.organizationId && (
								<div className="btn">
									<LassoSimpleButtonFull
										caption="CRM Integrations"
										onClick={() => {
											setShowCRMIntegrationsModal(true);
										}}
									/>
								</div>
							)}
						</div>
					</div>
					<div className="self-stretch flex-col justify-start items-start gap-10 flex">
						<div className="self-stretch  flex-col justify-start items-start gap-2 flex">
							<div className="w-[448px] text-gray-900 text-base font-semibold fo nt-['Hanken Grotesk'] leading-snug">
								Organization name
							</div>
							<input
								disabled={!isEditing}
								className="w-full transparent-input"
								placeholder={"Organization Name"}
								value={appStore.current.selectedOrganization!.organizationName}
								onChange={(e) => (appStore.current.selectedOrganizationName = e.target.value)}
							/>
						</div>
						<div className="self-stretch  flex-col justify-start items-start gap-2 flex">
							<div className="w-[448px] text-gray-900 text-base font-semibold fo nt-['Hanken Grotesk'] leading-snug">
								Verisk's XNAddress
							</div>
							<input
								disabled={!isEditing}
								className="w-full transparent-input"
								placeholder={"REBUILD_TEST"}
								value={appStore.current.selectedOrganization!.recipientsXNAddress}
								onChange={(e) => (appStore.current.selectedOrganization!.recipientsXNAddress = e.target.value)}
							/>
						</div>
						<div className="self-stretch flex-col justify-start items-start flex">
							<div className="justify-start items-start gap-1 inline-flex">
								<div className="text-justify text-gray-900 text-base font-semibold font-['Hanken Grotesk'] leading-snug">
									Login type
								</div>
								<div className="w-5 self-stretch pt-0.5 justify-start items-center gap-2.5 flex">
									<div className="w-5 h-5 justify-center items-center flex">
										<div className="w-5 h-5 relative flex-col justify-start items-start flex" />
									</div>
								</div>
							</div>
							<div className="self-stretch pt-2 justify-between items-end inline-flex">
								<div className="w-full justify-between items-center gap-2 flex ">
									<div className="justify-start items-center gap-1 flex min-h-[42px]">
										<LassoToggle
											disabled={!isEditing}
											checked={allowDomain}
											onChange={(val) => {
												setAllowDomain(val);
												if (!val) {
													appStore.current.selectedOrganizationAllowedDomain = "";
												}
											}}
										/>
										<div className="text-justify text-slate-600 text-base font-normal font-['Hanken Grotesk'] leading-normal">
											Allow domain login
										</div>
									</div>
									<div className="flex justify-center items-center">
										<div hidden={!allowDomain}>@</div>
										<input
											className="transparent-input"
											value={appStore.current.selectedOrganization.allowedDomain}
											hidden={!allowDomain}
											onChange={(e) => (appStore.current.selectedOrganizationAllowedDomain = e.target.value)}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="self-stretch flex-col justify-start items-start gap-2 flex">
							<div className="self-stretch text-justify text-gray-900 text-base font-semibold font-['Hanken Grotesk'] leading-snug">
								Description
							</div>
							<textarea
								disabled={!isEditing}
								className="w-full min-h-32 transparent-input"
								placeholder={"Organization Description"}
								value={appStore.current.selectedOrganization.organizationDescription}
								onChange={(e) => (appStore.current.selectedOrganizationDescription = e.target.value)}
							/>
						</div>
						<div className="self-stretch h-[150px] flex-col justify-start items-start gap-2 flex">
							<div className="self-stretch text-justify text-gray-900 text-base font-semibold font-['Hanken Grotesk'] leading-snug">
								AI Model
							</div>
							<textarea
								disabled={!isEditing}
								onChange={(e) => (appStore.current.selectedOrganizationAIModels = e.target.value)}
								className="w-full h-32 transparent-input"
								value={appStore.current.selectedOrganization.aiModels}
							/>
						</div>
						{/* <div className="self-stretch h-[150px] flex-col justify-start items-start gap-2 flex">
				<div className="self-stretch text-justify text-gray-900 text-base font-semibold font-['Hanken Grotesk'] leading-snug">Custom Settings</div>
				<textarea
					disabled={!isEditing}
					onChange={(e) => appStore.current.selectedOrganizationCustomSettings = e.target.value}
					className='w-full h-32 transparent-input'
					value={appStore.current.selectedOrganization.customSettings} />
			</div> */}
					</div>
				</div>
				{showFeatureFlagsModal && (
					<FeatureFlagsModal
						organizationId={appStore.current.selectedOrganization.organizationId}
						onClose={() => setShowFeatureFlagsModal(false)}
						onSave={handleSaveFeatureFlags}
					/>
				)}
				{showCRMIntegrationsModal && appStore.current.selectedOrganization?.organizationId && (
					<CRMIntegrationsModal
						organizationId={appStore.current.selectedOrganization.organizationId}
						organizationName={appStore.current.selectedOrganization.organizationName}
						onClose={() => setShowCRMIntegrationsModal(false)}
					/>
				)}
			</>
		)
	);
});

export const EstimaticsDataKeysAsObject: { [s: string]: string } = {
	projectName: "Project Name",
	estimateReferenceNumber: "Estimate Reference Number",
	requesterName: "Requester Name",
	email: "Email",
	phone: "Phone",
	propertyAddress: "Property Address",
	insuranceCarrier: "Insurance Carrier",
	claimNumber: "Claim Number",
	tpa: "TPA",
	dateCreated: "Date Created",
	dateContacted: "Date Contacted",
	dateOfLoss: "Date of Loss",
	office: "Office",
	assignee: "Assignee",
	status: "Status",
	approximateClaim: "Approximate Claim",
	links: "Links",
	notes: "Notes",
	files: "Files",
	category: "Category",
	typeOfLoss: "Type of Loss",
};

const LassoMVPOrganizationCustomization: React.FC = observer(() => {
	const allPossibleCategories = useRef(Object.keys(TicType1CatToCellMap).map((category) => ({ value: category, label: category })));
	const [availableCells, setAvailableCells] = useState<SelectOption[]>(Object.values(TicType1CatToCellMap).flat());
	const { organizationId } = useParams(); // 'param' matches the parameter name in the route
	const appStore = getAppStore();
	const [status, setStatus] = useState<{ name: string; color: string }[]>([]);
	const [name, setName] = useState("");
	const [office, setOffice] = useState("");
	const [offices, setOffices] = useState<string[]>([]);
	const [color, setColor] = useState("");
	const [tpa, setTpa] = useState("");
	const [tpas, setTpas] = useState<string[]>([]);
	const [carrier, setCarrier] = useState("");
	const [carriers, setCarriers] = useState<string[]>([]);
	const [extraField, setExtraField] = useState("");
	const [extraFields, setExtraFields] = useState<string[]>([]);
	const [XNAddress, setXNAddress] = useState("");
	const [XNAddresses, setXNAddresses] = useState<string[]>([]);
	const [requiredFields, setRequiredFields] = useState<string[]>([]);
	const [enabledNoteFlowTypes, setEnabledNoteFlowTypes] = useState<EnabledNoteFlowTypes>(DEFAULT_ENABLED_NOTE_FLOW_TYPES);
	const [generalRoomOrder, setGeneralRoomOrder] = useState<GeneralRoomOrder>(GeneralRoomOrder.START);
	const [customProfitAndOverhead, setCustomProfitAndOverhead] = useState<ProfitOverheadFormValues>({});
	const [showPDFExportConfigModal, setShowPDFExportConfigModal] = useState(false);
	useEffect(() => {
		logDebug("Custom settings from effect:", appStore.selectedOrganization);
		const settings = parseOrganizationCustomSettings(appStore.selectedOrganization?.customSettings) as {
			status?: { name: string; color: string }[];
			offices?: string[];
			tpas?: string[];
			carriers?: string[];
			extraFields?: string[];
			XNAddresses?: string[];
			requiredFields?: string[];
		};
		setStatus(settings.status || []);
		setOffices(settings.offices || []);
		setTpas(settings.tpas || []);
		setCarriers(settings.carriers || []);
		setExtraFields(settings.extraFields || []);
		setXNAddresses(settings.XNAddresses || []);
		setEnabledNoteFlowTypes(getEnabledNoteFlowTypesFromSettings(appStore.selectedOrganization?.customSettings));
		setGeneralRoomOrder(getGeneralRoomOrderFromSettings(appStore.selectedOrganization?.customSettings));
		const byEstimateType = getProfitOverheadMap(appStore.selectedOrganization?.customSettings);
		setCustomProfitAndOverhead(
			estimate_types.reduce<ProfitOverheadFormValues>((acc, estimateType) => {
				const entry = byEstimateType[estimateType];
				acc[estimateType] = {
					profit: entry?.profit,
					overhead: entry?.overhead,
				};
				return acc;
			}, {}),
		);
		if (settings.requiredFields) {
			logDebug("Required fields:", settings.requiredFields);
			setRequiredFields(settings.requiredFields);
		} else {
			setRequiredFields([]);
		}
		if (appStore.selectedOrganization?.organizationTPARules && appStore.selectedOrganization!.organizationTPARules!.length > 0) {
			localTPAStore.perTpaAndCarrierRules = appStore.selectedOrganization?.organizationTPARules;
		}
	}, [appStore.selectedOrganization?.customSettings]);

	const noNoteFlowTypesEnabled = !enabledNoteFlowTypes.reese && !enabledNoteFlowTypes.modules;

	return (
		<div className="border border-black max-h-[80vh] overflow-y-auto p-2">
			{/* PDF Export Config Button */}
			<div className="p-2">
				<LassoSimpleButtonFull caption="PDF Export Config" onClick={() => setShowPDFExportConfigModal(true)} />
			</div>
			<div className="p-2">
				<div className="font-semibold mb-1">Available note-taking flows</div>
				<div className="text-sm text-gray-600 mb-2">
					Toggle which note-taking flows are available to field users in the mobile app. Reese uses voice conversations;
					Modules uses guided surveys.
				</div>
				<div className="flex flex-col gap-2 max-w-[640px]">
					<div className="flex items-center gap-2">
						<LassoToggle
							checked={enabledNoteFlowTypes.reese}
							onChange={(v) => setEnabledNoteFlowTypes((prev) => ({ ...prev, reese: v }))}
						/>
						<div className="text-sm">Reese</div>
					</div>
					<div className="flex items-center gap-2">
						<LassoToggle
							checked={enabledNoteFlowTypes.modules}
							onChange={(v) => setEnabledNoteFlowTypes((prev) => ({ ...prev, modules: v }))}
						/>
						<div className="text-sm">Modules</div>
					</div>
				</div>
				{noNoteFlowTypesEnabled && (
					<div className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 max-w-[640px]">
						No note-taking flows are enabled. Field users won’t be able to take notes until at least one is turned on.
					</div>
				)}
			</div>
			<div className="p-2">
				<label className="block font-semibold" htmlFor="general-room-order">
					General Room placement
				</label>
				<div className="text-sm text-gray-600 mb-2">
					Controls whether the General Room appears as the first or last room in estimates for this organization.
				</div>
				<select
					id="general-room-order"
					className="border rounded px-2 py-1"
					value={generalRoomOrder}
					onChange={(e) => setGeneralRoomOrder(e.target.value as GeneralRoomOrder)}
				>
					<option value={GeneralRoomOrder.START}>Start (default)</option>
					<option value={GeneralRoomOrder.END}>End</option>
				</select>
			</div>
			{/* Status */}
			<div className="p-2">
				Status:{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={name}
					onChange={(e) => {
						setName(e.target.value);
					}}
				/>
				Color:{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={color}
					onChange={(e) => {
						setColor(e.target.value);
					}}
				/>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (name && color && !status.find((s) => s.name === name)) {
							setStatus([...status, { name, color }]);
							setColor("");
							setName("");
						}
					}}
				>
					add
				</button>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (name) {
							const index = status.findIndex((s) => s.name === name);
							if (index >= 0) {
								const newStatus = [...status];
								newStatus.splice(index, 1);
								setStatus(newStatus);
								setColor("");
								setName("");
							}
						}
					}}
				>
					remove
				</button>
				<div>Status Preview</div>
				<textarea readOnly className="border w-full h-32" value={JSON.stringify(status)}></textarea>
			</div>
			{/* Office : TODO ::we need to combine office and XAAddress to an object it's not like that to support backward compatability with Estimatics. Once we'll drop it as a product we need to implement this */}
			<div className="p-2">
				Office:{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={office}
					onChange={(e) => {
						setOffice(e.target.value);
					}}
				/>
				XNAddress(opt):{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={XNAddress}
					onChange={(e) => {
						setXNAddress(e.target.value);
					}}
				/>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (office && !offices.includes(office)) {
							const newOffices = [...offices, office];
							let newXNAddresses: string[] = [];
							if (offices.length === XNAddresses.length) {
								newXNAddresses = [...XNAddresses, XNAddress];
							} else {
								for (let i = 0; i < offices.length; i++) {
									newXNAddresses.push(XNAddresses[i] || "");
								}
								newXNAddresses.push(XNAddress);
							}
							setXNAddresses(newXNAddresses);
							setXNAddress("");
							setOffices(newOffices);
							setOffice("");
						}
					}}
				>
					add
				</button>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (office) {
							const index = offices.findIndex((s) => s === office);
							if (index >= 0) {
								const newOffices = [...offices];
								newOffices.splice(index, 1);
								const newXNAddresses = [...XNAddresses];
								newXNAddresses.splice(index, 1);
								setXNAddresses(newXNAddresses);
								setXNAddress("");
								setOffices(newOffices);
								setOffice("");
							}
						}
					}}
				>
					remove
				</button>
				<div>Offices Preview</div>
				<textarea readOnly className="border w-full h-16" value={JSON.stringify(offices)}></textarea>
				<textarea readOnly className="border w-full h-16" value={JSON.stringify(XNAddresses)}></textarea>
			</div>
			{/* TPA */}
			<div className="p-2">
				TPA:{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={tpa}
					onChange={(e) => {
						setTpa(e.target.value);
					}}
				/>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (tpa && !tpas.includes(tpa)) {
							setTpas([...tpas, tpa]);
							setTpa("");
						}
					}}
				>
					add
				</button>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (tpa) {
							const index = tpas.findIndex((s) => s === tpa);
							if (index >= 0) {
								const newTPAs = [...tpas];
								newTPAs.splice(index, 1);
								setTpas(newTPAs);
								setTpa("");
							}
						}
					}}
				>
					remove
				</button>
				<div>TPA Preview - (if filled TPA will be combobox otherwise text field)</div>
				<textarea readOnly className="border w-full h-32" value={JSON.stringify(tpas)}></textarea>
			</div>
			{/* Carrier List */}
			<div className="p-2">
				Carrier:{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={carrier}
					onChange={(e) => {
						setCarrier(e.target.value);
					}}
				/>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (carrier && !carriers.includes(carrier)) {
							setCarriers([...carriers, carrier]);
							setCarrier("");
						}
					}}
				>
					add
				</button>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (carrier) {
							const index = carriers.findIndex((s) => s === carrier);
							if (index >= 0) {
								const newCarriers = [...carriers];
								newCarriers.splice(index, 1);
								setCarriers(newCarriers);
								setCarrier("");
							}
						}
					}}
				>
					remove
				</button>
				<div>Carrier Preview</div>
				<textarea readOnly className="border w-full h-32" value={JSON.stringify(carriers)}></textarea>
			</div>
			{/* Extra fields List */}
			<div className="p-2">
				Extra fields:{" "}
				<input
					type="text"
					className="border w-16 px-1"
					value={extraField}
					onChange={(e) => {
						setExtraField(e.target.value);
					}}
				/>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (extraField && !extraFields.includes(extraField)) {
							setExtraFields([...extraFields, extraField]);
							setExtraField("");
						}
					}}
				>
					add
				</button>
				<button
					className="bg-sky-100 rounded px-1 mx-1"
					onClick={() => {
						if (extraField) {
							const index = extraFields.findIndex((s) => s === extraField);
							if (index >= 0) {
								const newExtraFields = [...extraFields];
								newExtraFields.splice(index, 1);
								setExtraFields(newExtraFields);
								setExtraField("");
							}
						}
					}}
				>
					remove
				</button>
				<div> Extra fields Preview</div>
				<textarea readOnly className="border w-full h-32" value={JSON.stringify(extraFields)}></textarea>
			</div>
			<div className="p-2">
				<div className="font-semibold mb-2">Custom Overhead/Profit by Estimate Type</div>
				<div className="border rounded overflow-hidden max-w-[640px]">
					<div className="grid grid-cols-[2fr_1fr_1fr] bg-gray-100 px-2 py-1 font-semibold text-sm">
						<div>Estimate Type</div>
						<div>Overhead %</div>
						<div>Profit %</div>
					</div>
					{estimate_types.map((estimateType) => (
						<div key={estimateType} className="grid grid-cols-[2fr_1fr_1fr] items-center px-2 py-2 border-t">
							<div className="text-sm">{estimateType}</div>
							<input
								id={`custom-overhead-percent-${estimateType}`}
								type="text"
								inputMode="numeric"
								autoComplete="off"
								className="border rounded px-2 py-1 mr-2 w-20"
								value={customProfitAndOverhead[estimateType]?.overhead ?? ""}
								onChange={(e) => {
									const digits = e.target.value.replace(/\D/g, "");
									const next = digits === "" ? undefined : Number(digits);
									setCustomProfitAndOverhead((prev) => ({
										...prev,
										[estimateType]: {
											...(prev[estimateType] ?? {}),
											overhead: next,
										},
									}));
								}}
								placeholder="10%"
							/>
							<input
								id={`custom-profit-percent-${estimateType}`}
								type="text"
								inputMode="numeric"
								autoComplete="off"
								className="border rounded px-2 py-1 w-20"
								value={customProfitAndOverhead[estimateType]?.profit ?? ""}
								onChange={(e) => {
									const digits = e.target.value.replace(/\D/g, "");
									const next = digits === "" ? undefined : Number(digits);
									setCustomProfitAndOverhead((prev) => ({
										...prev,
										[estimateType]: {
											...(prev[estimateType] ?? {}),
											profit: next,
										},
									}));
								}}
								placeholder="10%"
							/>
						</div>
					))}
				</div>
			</div>
			<div className="w-full">
				<div className="m-2">Required fields</div>
				<div className="h-32 overflow-y-auto border border-black m-2">
					{Object.keys(EstimaticsDataKeysAsObject).map((key, index) => {
						const value = EstimaticsDataKeysAsObject[key];
						return (
							<div key={key + requiredFields.includes(key)} className="justify-start items-center gap-4">
								<div className="flex m-2">
									<LassoToggle
										checked={requiredFields.includes(key) || index === 0}
										disabled={index === 0}
										onChange={(v) => {
											if (v) {
												if (!requiredFields.includes(key)) {
													setRequiredFields([...requiredFields, key]);
												}
											} else {
												const index = requiredFields.findIndex((r) => r === key);
												if (index >= 0) {
													const newRequiredFields = [...requiredFields];
													newRequiredFields.splice(index, 1);
													setRequiredFields(newRequiredFields);
												}
											}
										}}
									/>
									<div className="mx-2">{value}</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
			<button
				className="bg-sky-100 p-2 rounded-xl"
				onClick={async () => {
					appStore.setCustomeSettings(
						buildOrganizationCustomSettings(appStore.selectedOrganization?.customSettings, {
							status,
							offices,
							XNAddresses,
							tpas,
							carriers,
							extraFields,
							requiredFields,
							enabledNoteFlowTypes,
							generalRoomOrder,
							customProfitAndOverhead,
						}),
					);
					logDebug("Custom settings:", appStore.selectedOrganization?.customSettings);
					const yesNo = window.confirm(
						"this will only set the cusotom settings, not other changes to the organization details. Do you want to save the custom settings?",
					);
					if (yesNo) {
						const newOrg = await RestAPI.fetchAPI("/rest/access/editOrganization", "POST", {
							organization: {
								organizationId: appStore.selectedOrganization!.organizationId,
								customSettings: appStore.selectedOrganization!.customSettings,
							},
						});
						await appStore.loadOrganizations(true);
					}
				}}
			>
				Save Custom Settings
			</button>
			{localTPAStore.showTPA && (
				<TopMostPortal>
					<div className="bg-white w-[840px] h-[500px]">
						<div className="flex w-full h-6 bg-gray-300 justify-between">
							<div>&nbsp;</div>
							<div onClick={() => (localTPAStore.showTPA = false)} className="btn ">
								<img className="w-4 h-4 m-1 justify-center items-center flex" src="/assets/ui/x.svg"></img>
							</div>
						</div>
						<div className="h-full  bg-gray-100 pt-2">
							<div className="flex jsutify-between items-center gap-2">
								<div>TPA:</div>
								<select
									value={localTPAStore.tpa}
									className=""
									onChange={(e) => {
										localTPAStore.tpa = e.target.value;
									}}
								>
									{["", ...tpas].map((tpa, index) => {
										return (
											<option key={tpa} value={tpa}>
												{tpa}
											</option>
										);
									})}
								</select>
								<div>Carrier:</div>
								<select
									className=""
									value={localTPAStore.carrier}
									onChange={(e) => {
										localTPAStore.carrier = e.target.value;
									}}
								>
									{["", ...carriers].map((carrier, index) => {
										return (
											<option key={carrier} value={carrier}>
												{carrier}
											</option>
										);
									})}
								</select>
								<div>Rule:</div>
								<select
									className=""
									value={localTPAStore.newRuleType}
									onChange={(e) => {
										localTPAStore.newRuleType = e.target.value;
									}}
								>
									{["", ...gTPARuleTypes].map((rule, index) => {
										return (
											<option key={rule} value={rule}>
												{rule}
											</option>
										);
									})}
								</select>
								<button
									onClick={() => localTPAStore.importTPA()}
									className={`${localTPAStore.defaultTPA < 0 ? "bg-gray-500" : "bg-blue-500"} rounded text-white px-2`}
									disabled={localTPAStore.defaultTPA < 0}
								>
									Import TPA
								</button>
								<button
									onClick={() => localTPAStore.importCarrier()}
									className={`${
										localTPAStore.defaultCarrier < 0 ? "bg-gray-500" : "bg-blue-500"
									} rounded text-white px-2`}
									disabled={localTPAStore.defaultCarrier < 0}
								>
									Import Carrier
								</button>
								<button
									onClick={async () => {
										localTPAStore.showFetchingAnimation = true;
										try {
											const ret = await localTPAStore.saveOrganizationTPARules(organizationId!);
											if (ret) {
												toastNotify("TPA rules saved", false);
											} else {
												toastNotify("Error saving TPA rules", true);
											}
										} catch (err) {
											toastNotify("Error saving TPA rules", true);
										}
										localTPAStore.showFetchingAnimation = false;
									}}
									className={`bg-blue-500 rounded text-white px-2`}
								>
									Save All
								</button>

								{/* <button
									onClick={async () => {
										if (!localTPAStore.tpa && !localTPAStore.carrier) {
											alert("Please select at least one TPA or Carrier");
											return;
										}
										if (!localTPAStore.rule) {
											alert("Please select a rule");
											return;
										}
										localTPAStore.newRuleType = localTPAStore.rule;
									}}
								>
									Edit New Rule
								</button> */}
								{/* <Select
									isClearable
									value={hackishTPAStore.selectedCategory}
									onChange={(e) => {
										hackishTPAStore.selectedCategory = e;
										hackishTPAStore.search = "";
									}}
									placeholder="Select category"
									className="w-[198px]"
									options={allPossibleCategories.current}
									// styles={customStyles}
								></Select>
								<Select
									isClearable
									value={hackishTPAStore.selectedCell}
									onChange={(e) => {
										hackishTPAStore.selectedCell = e;
										hackishTPAStore.search = "";
									}}
									placeholder="Select cell"
									// styles={customStyles}
									className="w-[198px]"
									options={availableCells}
								></Select> */}
							</div>
							<div className="h-full ">
								<NewRuleScreen />
							</div>
						</div>
					</div>
				</TopMostPortal>
			)}
			{showPDFExportConfigModal && organizationId && appStore.selectedOrganization && (
				<PDFExportConfigModal
					organizationId={organizationId}
					organizationName={appStore.selectedOrganization.organizationName}
					availableOffices={offices}
					onClose={() => setShowPDFExportConfigModal(false)}
				/>
			)}
		</div>
	);
});

const LassoMVPOrganizationSettingsPage = observer(() => {
	const { organizationId } = useParams(); // 'param' matches the parameter name in the route

	const appStore = getAppStore();
	const navigate = useNavigate();
	const setSelectedOrganization = async () => {
		localTPAStore.showFetchingAnimation = true;
		try {
			await appStore.loadOrganizations();
			const org = appStore.organizations.find((o) => o.organizationId === organizationId);
			if (org) {
				appStore.setSelectedOrganization(org);
				appStore.setBreadcrumbPath(["Organizations", org.organizationName]);
			}
		} catch (err) {}
		localTPAStore.showFetchingAnimation = false;
	};
	useEffect(() => {
		if (organizationId === "new") {
			appStore.setBreadcrumbPath(["Organizations", "New Organization"]);
			appStore.setNewOrganization();
		} else {
			setSelectedOrganization();
		}
	}, []);

	const onCreate = async () => {
		let newOrg: IOrganizationCreateObject | null = null;
		localTPAStore.showFetchingAnimation = true;
		try {
			if (organizationId === "new") {
				logDebug("Creating new organization", appStore.selectedOrganization?.customSettings);
				newOrg = await RestAPI.fetchAPI("/rest/access/createOrganization", "POST", {
					organization: {
						customSettings: appStore.selectedOrganization!.customSettings,
						aiModels: appStore.selectedOrganization!.aiModels,
						organizationAddress: "",
						organizationLogo: appStore.selectedOrganization!.organizationLogo,
						organizationName: appStore.selectedOrganization!.organizationName,
						recipientsXNAddress: appStore.selectedOrganization!.recipientsXNAddress || "REBUILD_TEST",
						organizationDescription: appStore.selectedOrganization!.organizationDescription,
					} as IOrganizationCreateObject,
					users: appStore.selectedOrganization?.users,
					allowedDomain: appStore.selectedOrganization!.allowedDomain,
				});
				await appStore.loadOrganizations(true);
				logDebug("Organization:", newOrg);
			} else {
				newOrg = await RestAPI.fetchAPI("/rest/access/editOrganization", "POST", {
					organization: {
						organizationId: appStore.selectedOrganization!.organizationId,
						organizationName: appStore.selectedOrganization!.organizationName,
						organizationDescription: appStore.selectedOrganization!.organizationDescription,
						organizationLogo: appStore.selectedOrganization!.organizationLogo,
						aiModels: appStore.selectedOrganization!.aiModels,
						customSettings: appStore.selectedOrganization!.customSettings,
						recipientsXNAddress: appStore.selectedOrganization!.recipientsXNAddress || "REBUILD_TEST",
					},
					// editOrganization only *inserts* users (existing-member edits go through
					// editUser). Send only brand-new users — those without a lassoUserId — otherwise
					// the backend rejects the whole call because existing members "already exist".
					users: appStore.selectedOrganization?.users?.filter((u) => !(u as IOrganizationUser).lassoUserId),
					allowedDomain: appStore.selectedOrganization!.allowedDomain,
				});
				await appStore.loadOrganizations(true);
			}
		} catch (err) {
			logError("Error creating organization", err);
		}
		localTPAStore.showFetchingAnimation = false;
		// TODO :: make sure there was no errors
		if (newOrg) {
			navigate("/");
		} else {
			// TODO :: show error message
			alert("Error creating organization");
		}
	};

	return (
		<>
			{appStore.selectedOrganization && (
				<div>
					<div className="flex w-full justify-center">
						<div>
							<LassoMVPUserTable />
						</div>
						<div className="ml-8">
							<LassoOrganizationSettings startInEditMode={organizationId === "new"} />
						</div>
						<div>
							<LassoMVPOrganizationCustomization />
						</div>
					</div>

					<div className="w-full h-[72px] bg-white border-t border-gray-200 flex-col justify-start items-center inline-flex fixed bottom-0 left-0">
						<div className="self-stretch h-[72px] px-8 justify-between items-center inline-flex">
							<LassoSimpleButtonFull caption="Save changes" onClick={onCreate} />
							<LassoSimpleButtonFull
								caption="Cancel & Back"
								customButtonStyle={{ backgroundColor: "white", border: "1px solid #EAECF0" }}
								customTextStyle={{ color: "#344054" }}
							/>
						</div>
					</div>
				</div>
			)}

			{localTPAStore.showFetchingAnimation && <LoadingAnimation />}
		</>
	);
});

export default LassoMVPOrganizationSettingsPage;
